import { Category, Store } from "../../../../types";
import { getCategoryReport } from "../../../../lib/admin";
import { useReportPage } from "../hooks/useReportPage";
import StoreReportLoadingRows from '../shared/StoreReportLoadingRows';
import StoreReportRow from '../shared/StoreReportRow';
import StoreReportRowHeader from '../shared/StoreReportRowHeader';
import StoreReportCategoryDetails from './StoreReportCategoryDetails';
import StoreReportToolbar from '../StoreReportToolbar';
import { EntityLabel } from '../ForceSyncSelectedButton';
import StoreReportNoResultsMessage from '../StoreReportNoResultsMessage';
import Notification from '../../../global/Notification';
import Pagination from '../../../global/Pagination';

const CATEGORIES_PER_PAGE = 25;
const MAX_SELECTED_CATEGORIES = 100;
const ENTITY_LABEL: EntityLabel = { one: "category", many: "categories" };

type Props =
    {
        store: Store
        onForceSyncAll?: () => Promise<string>
        onForceSyncSelection?: (entityIds: number[]) => Promise<string>
        isSyncing?: boolean
    }

export default function StoreReportCategoriesList({ store, onForceSyncAll, onForceSyncSelection, isSyncing }: Props) {
    const report = useReportPage<Category>({
        storeId: store.id,
        pageSize: CATEGORIES_PER_PAGE,
        maxSelected: MAX_SELECTED_CATEGORIES,
        selectionLimitNotice: `You can select up to ${MAX_SELECTED_CATEGORIES} ${ENTITY_LABEL.many} at a time.`,
        fetchPage: async (page, pageSize, search, status) => {
            const result = await getCategoryReport(store.id, page, pageSize, search, status);
            return { rows: result.categories ?? [], total: result.pagination?.total ?? 0 };
        },
        isSyncing,
        onForceSyncSelection,
    });

    const openInShopify = (shopifyId: string) => {
        const storeSplit = store.storeUrl.split('.');
        const storeURL = storeSplit[0];
        window.open(`https://admin.shopify.com/store/${storeURL}/collections/${shopifyId}`, '_blank');
    }

    return (
        <div className="products products-report">
            <StoreReportToolbar
                search={report.search}
                setSearch={report.updateSearch}
                status={report.status}
                setStatus={report.updateStatus}
                onRefresh={report.refresh}
                isLoading={report.isLoading}
                onForceSyncAll={onForceSyncAll}
                onForceSyncSelected={onForceSyncSelection && report.forceSyncSelection}
                selectedCount={report.selectedIds.length}
                entityLabel={ENTITY_LABEL}
                onClearSelection={report.clearSelection}
                isSyncing={isSyncing}
            />
            {!report.retrieveError && <StoreReportRowHeader allSelected={report.allOnPageSelected} onToggleAll={report.toggleAllOnPage} />}
            {report.isLoading ? (
                <StoreReportLoadingRows />
            ) : report.retrieveError ? (
                <p className="sync-error-retrieve">Currently unable to retrieve the information. Please try again later.</p>
            ) : report.rows.length === 0 ? (
                report.search || report.status ? (
                    <StoreReportNoResultsMessage
                        reportName="Categories"
                        searchTerm={report.search}
                        onClear={report.clearFilters}
                    />
                ) : (
                    <p className="no-products-message">No categories available to display</p>
                )
            ) : (
                <div>
                    {report.rows.map((category) => (
                        <div key={category.id} className="products-list-view">
                            <StoreReportRow
                                row={category}
                                openInShopify={openInShopify}
                                selected={report.selectedIds.includes(category.id)}
                                onToggleSelected={report.toggleSelected}
                                isExpanded={report.expandedId === category.id}
                                onToggleDetails={report.toggleDetails}
                                renderDetails={() => <StoreReportCategoryDetails storeId={store.id} category={category} />}
                            ></StoreReportRow>
                        </div>
                    ))}

                    <Pagination
                        pageSize={CATEGORIES_PER_PAGE}
                        totalItems={report.total}
                        paginate={report.setCurrentPage}
                        currentPage={report.currentPage}
                    />
                </div>
            )}
            <Notification message={report.selectionNotice} type="error" onClose={report.dismissNotice} />
        </div>
    );
}